<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { type Patient } from '@/api/patients';
import {
  clinicalRecordsApi,
  type ClinicalShift,
  type ClinicalTaskRecordState,
} from '@/api/clinical-records';
import { useFileDownload } from '@/composables/useFileDownload';
import { getCareerColor } from '@/utils/careers';
import { AI_SUMMARY_AUTHOR_LINE, clinicalAuthorName, SYSTEM_AI_AUTHOR } from '@/utils/clinical-author';
import GoBackButton from '@/components/go-back-button.vue';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';
import type { VitalSignRecord } from '@/api/vital-sign-records';

const props = defineProps<{
  patient: Patient;
}>();

const { downloadFile } = useFileDownload();

const DEFAULT_DAYS_BACK = 30;
const MAX_FILTER_DAYS = 40;
const DATE_PAD_LENGTH = 2;
const MS_PER_DAY = 86_400_000;

function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(DATE_PAD_LENGTH, '0');
  const day = String(date.getDate()).padStart(DATE_PAD_LENGTH, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function defaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_DAYS_BACK);
  return formatDate(date);
}

function defaultEndDate(): string {
  return formatDate(new Date());
}

const startDate = ref(defaultStartDate());
const endDate = ref(defaultEndDate());
const downloadingPdf = ref(false);
const includeImagesInPdf = ref(false);
const expandedShifts = ref<Set<number>>(new Set());

// Parses 'YYYY-MM-DD' as UTC midnight. Both sides share the same parsing, so the
// day-diff is timezone-stable. Do not switch to local parsing without revisiting
// validateDateRange.
function parseRange(startValue: string, endValue: string): [number, number] | null {
  if (!startValue || !endValue) return null;
  const start = new Date(startValue).getTime();
  const end = new Date(endValue).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return [start, end];
}

function validateDateRange(startValue: string, endValue: string): string | null {
  const range = parseRange(startValue, endValue);
  if (!range) return null; // Empty/unparseable inputs — unreachable via UI (not clearable).
  const [start, end] = range;
  if (start > end) return 'La fecha "Desde" debe ser anterior a "Hasta".';
  if (Math.round((end - start) / MS_PER_DAY) > MAX_FILTER_DAYS) {
    return `El rango máximo permitido es de ${MAX_FILTER_DAYS} días.`;
  }
  return null;
}

// Returns null when both inputs are filled AND the range is <= MAX_FILTER_DAYS days.
// Inputs are not clearable, so both values are always present from the UI.
// An error here blocks the query (via `enabled`) and the PDF button.
const dateRangeError = computed<string | null>(
  () => validateDateRange(startDate.value, endDate.value),
);

const isRangeValid = computed(() => dateRangeError.value === null);

const { data: shifts, isFetching } = useQuery({
  queryKey: computed(() => ['clinicalRecords', props.patient.id, startDate.value, endDate.value]),
  queryFn: () => clinicalRecordsApi.index(
    props.patient.id,
    startDate.value || undefined,
    endDate.value || undefined,
  ),
  enabled: isRangeValid,
  initialData: [],
});

// Anulados stay in the ficha: hidden until asked for, then crossed out with
// who anuló them, when and why. The PDF always prints them, marked.
const showVoided = ref(false);

function shiftVoidedCount(shift: ClinicalShift): number {
  return [
    ...shift.medicalRecords, ...shift.reportImages, ...shift.reportTexts,
    ...shift.vitalSignRecords, ...shift.incidentRecords, ...shift.taskRecords,
  ].filter(record => record.voided).length;
}

const voidedCount = computed(
  () => (shifts.value ?? []).reduce((count, shift) => count + shiftVoidedCount(shift), 0),
);

function visible<T extends { voided: boolean }>(records: T[]): T[] {
  return showVoided.value ? records : records.filter(record => !record.voided);
}

function vitalSignCorrection(record: VitalSignRecord) {
  return record.bloodPressureRecordSystolic ? 'bloodPressure' : 'value';
}

const VOIDED_CLASS = 'text-decoration-line-through text-medium-emphasis';

function toggleExpand(shiftId: number) {
  const next = new Set(expandedShifts.value);
  if (next.has(shiftId)) {
    next.delete(shiftId);
  } else {
    next.add(shiftId);
  }
  expandedShifts.value = next;
}

function expandAll() {
  expandedShifts.value = new Set(shifts.value?.map((s: ClinicalShift) => s.id) ?? []);
}

function collapseAll() {
  expandedShifts.value = new Set();
}

function totalRecords(shift: ClinicalShift): number {
  return shift.reportImagesCount +
    shift.reportTextsCount +
    shift.vitalSignRecordsCount +
    shift.incidentRecordsCount +
    shift.taskRecordsCount +
    (shift.hasMedicalRecord ? 1 : 0);
}

const TASK_STATE_LABELS: Record<ClinicalTaskRecordState, string> = {
  pending: 'Pendiente',
  completed: 'Completada',
  incomplete: 'No realizada',
};

const TASK_STATE_COLORS: Record<ClinicalTaskRecordState, string> = {
  pending: 'primary',
  completed: 'success',
  incomplete: 'error',
};

function taskStateLabel(state: ClinicalTaskRecordState): string {
  return TASK_STATE_LABELS[state] ?? state;
}

function taskStateColor(state: ClinicalTaskRecordState): string {
  return TASK_STATE_COLORS[state] ?? 'grey';
}

async function downloadPdf() {
  if (!isRangeValid.value) return;
  downloadingPdf.value = true;
  try {
    const response = await clinicalRecordsApi.indexAsPdf(
      props.patient.id,
      startDate.value || undefined,
      endDate.value || undefined,
      includeImagesInPdf.value,
    );
    downloadFile(response);
  } finally {
    downloadingPdf.value = false;
  }
}

const medicalRecordFields = [
  { key: 'reason', label: 'Motivo de consulta' },
  { key: 'diagnosis', label: 'Diagnóstico' },
  { key: 'treatment', label: 'Tratamiento' },
  { key: 'physicalExam', label: 'Examen físico' },
  { key: 'currentIllness', label: 'Enfermedad actual' },
  { key: 'personalHistory', label: 'Historia personal' },
] as const;
</script>
<template>
  <v-container class="v-col-12 v-col-md-10">
    <go-back-button :path="`/patients/${patient.id}`" />

    <v-card class="mt-4">
      <v-toolbar
        color="white"
        flat
      >
        <v-toolbar-title class="font-weight-bold">
          Ficha Clínica de {{ patient.fullName }}
        </v-toolbar-title>
      </v-toolbar>

      <v-toolbar
        color="white"
        flat
      >
        <v-row
          class="align-center px-2"
          no-gutters
        >
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pa-2"
          >
            <v-text-field
              v-model="startDate"
              type="date"
              label="Desde"
              density="compact"
              variant="outlined"
              hide-details
              :error="!isRangeValid"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pa-2"
          >
            <v-text-field
              v-model="endDate"
              type="date"
              label="Hasta"
              density="compact"
              variant="outlined"
              hide-details
              :error="!isRangeValid"
            />
          </v-col>
          <v-spacer />
          <v-col
            v-if="voidedCount > 0"
            cols="auto"
            class="pa-2"
          >
            <show-voided-switch
              v-model="showVoided"
              :count="voidedCount"
            />
          </v-col>
          <v-col
            cols="auto"
            class="pa-2"
          >
            <v-checkbox
              v-model="includeImagesInPdf"
              label="Incluir imágenes en PDF"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col
            cols="auto"
            class="pa-2"
          >
            <v-btn
              color="primary"
              variant="outlined"
              :loading="downloadingPdf"
              :disabled="!isRangeValid"
              prepend-icon="mdi-file-pdf-box"
              @click="downloadPdf"
            >
              Descargar PDF
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>

      <v-card-text>
        <!-- Toolbar for timeline -->
        <div class="d-flex align-center mb-4">
          <h3 class="text-h6 font-weight-bold">
            Historial Clínico
          </h3>
          <v-spacer />
          <v-btn
            icon="mdi-arrow-expand-vertical"
            variant="text"
            size="small"
            :disabled="!shifts?.length"
            @click="expandAll"
          />
          <v-btn
            icon="mdi-arrow-collapse-vertical"
            variant="text"
            size="small"
            :disabled="!shifts?.length"
            @click="collapseAll"
          />
        </div>

        <v-alert
          v-if="dateRangeError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ dateRangeError }}
        </v-alert>

        <v-progress-linear
          v-if="isFetching"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <!-- Empty state -->
        <v-alert
          v-if="!isFetching && (!shifts || shifts.length === 0)"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          No hay registros clínicos para el período seleccionado.
        </v-alert>

        <!-- Timeline -->
        <div
          v-for="shift in shifts"
          :key="shift.id"
          class="mb-3"
        >
          <!-- Shift header card -->
          <v-card
            class="shift-card"
            :class="{ 'shift-card--expanded': expandedShifts.has(shift.id) }"
            @click="toggleExpand(shift.id)"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <!-- Left: date, caregiver -->
                <div class="flex-grow-1">
                  <div class="d-flex align-center">
                    <v-icon
                      size="18"
                      class="mr-2"
                      color="primary"
                    >
                      mdi-calendar
                    </v-icon>
                    <span class="text-subtitle-2 font-weight-bold">
                      {{ shift.humanDate }}
                    </span>
                    <span class="ml-2 text-body-2 text-medium-emphasis">
                      {{ shift.humanTime }}
                    </span>
                  </div>
                  <div class="d-flex align-center mt-1">
                    <span class="text-body-2 mr-2">{{ shift.caregiverFullName }}</span>
                    <v-chip
                      size="x-small"
                      :color="getCareerColor(shift.caregiverCareer)"
                    >
                      {{ shift.caregiverHumanCareer }}
                    </v-chip>
                  </div>
                </div>
                <!-- Right: record badges -->
                <div class="d-flex flex-wrap ga-1 justify-end align-center">
                  <v-chip
                    v-if="shift.reportImagesCount > 0"
                    size="x-small"
                    variant="tonal"
                    prepend-icon="mdi-camera"
                  >
                    {{ shift.reportImagesCount }}
                  </v-chip>
                  <v-chip
                    v-if="shift.reportTextsCount > 0"
                    size="x-small"
                    variant="tonal"
                    prepend-icon="mdi-text-box"
                  >
                    {{ shift.reportTextsCount }}
                  </v-chip>
                  <v-chip
                    v-if="shift.vitalSignRecordsCount > 0"
                    size="x-small"
                    variant="tonal"
                    color="success"
                    prepend-icon="mdi-heart-pulse"
                  >
                    {{ shift.vitalSignRecordsCount }}
                  </v-chip>
                  <v-chip
                    v-if="shift.incidentRecordsCount > 0"
                    size="x-small"
                    variant="tonal"
                    color="error"
                    prepend-icon="mdi-alert"
                  >
                    {{ shift.incidentRecordsCount }}
                  </v-chip>
                  <v-chip
                    v-if="shift.hasMedicalRecord"
                    size="x-small"
                    variant="tonal"
                    color="info"
                    prepend-icon="mdi-stethoscope"
                  >
                    Registro médico
                  </v-chip>
                  <v-chip
                    v-if="shift.taskRecordsCount > 0"
                    size="x-small"
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-clipboard-check"
                  >
                    {{ shift.taskRecordsCount }}
                  </v-chip>
                  <v-chip
                    v-if="totalRecords(shift) === 0"
                    size="x-small"
                    variant="tonal"
                    color="grey"
                  >
                    Sin registros
                  </v-chip>
                  <v-icon
                    class="ml-2"
                    :icon="expandedShifts.has(shift.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Expanded content -->
          <v-expand-transition>
            <div v-if="expandedShifts.has(shift.id)">
              <v-card
                class="ml-4 mt-0 expanded-content"
                variant="outlined"
              >
                <v-card-text>
                  <!-- AI Summary -->
                  <v-alert
                    v-if="shift.reportImagesSummary"
                    type="info"
                    variant="tonal"
                    class="mb-4"
                    density="compact"
                  >
                    <template #prepend>
                      <v-icon icon="mdi-robot" />
                    </template>
                    <strong>Resumen IA:</strong> {{ shift.reportImagesSummary }}
                    <div class="text-caption text-medium-emphasis mt-1">
                      {{ AI_SUMMARY_AUTHOR_LINE }}
                    </div>
                  </v-alert>

                  <!-- Medical Records (the one in force, and anulados on demand) -->
                  <div
                    v-for="medicalRecord in visible(shift.medicalRecords)"
                    :key="medicalRecord.id"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                        color="info"
                      >
                        mdi-stethoscope
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">Registro Médico</span>
                      <clinical-record-history
                        item-type="MedicalRecord"
                        :item-id="medicalRecord.id"
                        :edited="medicalRecord.edited"
                        :label="`Registro médico · ${shift.humanDate}`"
                      />
                      <clinical-record-void
                        item-type="MedicalRecord"
                        :item-id="medicalRecord.id"
                        :can-void="medicalRecord.canVoid"
                        :label="`Registro médico · ${shift.humanDate}`"
                      />
                    </div>
                    <v-card
                      :variant="medicalRecord.voided ? 'outlined' : 'tonal'"
                      :color="medicalRecord.voided ? undefined : 'info'"
                      class="pa-3"
                    >
                      <clinical-author-line
                        :author="medicalRecord.author"
                        :at="medicalRecord.humanCreatedAt"
                        class="mb-2"
                      />
                      <clinical-void-mark
                        :voided="medicalRecord.voided"
                        :details="medicalRecord.voidDetails"
                        class="mb-2"
                      />
                      <template
                        v-for="field in medicalRecordFields"
                        :key="field.key"
                      >
                        <div
                          v-if="medicalRecord[field.key]"
                          class="mb-2"
                        >
                          <span class="font-weight-bold text-body-2">{{ field.label }}:</span>
                          <p
                            class="text-body-2 pre-wrap mt-0 mb-0"
                            :class="{ [VOIDED_CLASS]: medicalRecord.voided }"
                          >
                            {{ medicalRecord[field.key] }}
                          </p>
                        </div>
                      </template>
                    </v-card>
                  </div>

                  <!-- Report Images -->
                  <div
                    v-if="visible(shift.reportImages).length > 0"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                      >
                        mdi-camera
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">
                        Registros Visuales ({{ visible(shift.reportImages).length }})
                      </span>
                    </div>
                    <v-row>
                      <v-col
                        v-for="image in visible(shift.reportImages)"
                        :key="image.id"
                        cols="6"
                        sm="4"
                        md="3"
                      >
                        <v-dialog max-width="700">
                          <template #activator="{ props: activatorProps }">
                            <v-card
                              v-bind="activatorProps"
                              class="image-thumbnail"
                            >
                              <v-img
                                :src="image.imageUrlLarge"
                                height="120"
                                :class="{ 'voided-image': image.voided }"
                                cover
                              />
                              <v-card-text class="pa-2 text-caption text-medium-emphasis">
                                {{ image.humanCreatedAt }}
                                <div v-if="image.author">
                                  {{ clinicalAuthorName(image.author) }}
                                </div>
                                <v-chip
                                  v-if="image.voided"
                                  size="x-small"
                                  variant="tonal"
                                  color="error"
                                  label
                                >
                                  Anulado
                                </v-chip>
                              </v-card-text>
                            </v-card>
                          </template>
                          <template #default="{ isActive }">
                            <v-card>
                              <v-card-title class="d-flex justify-space-between align-center">
                                <span>Registro Visual</span>
                                <div class="d-flex align-center">
                                  <clinical-record-void
                                    item-type="ReportImage"
                                    :item-id="image.id"
                                    :can-void="image.canVoid"
                                    :label="`Registro visual · ${image.humanCreatedAt}`"
                                    @voided="isActive.value = false"
                                  />
                                  <v-btn
                                    icon="mdi-close"
                                    variant="text"
                                    aria-label="Cerrar"
                                    @click="isActive.value = false"
                                  />
                                </div>
                              </v-card-title>
                              <v-card-text>
                                <v-img
                                  :src="image.imageUrlLarge"
                                  max-height="500"
                                  :class="{ 'voided-image': image.voided }"
                                  contain
                                />
                                <clinical-author-line
                                  :author="image.author"
                                  :at="image.humanCreatedAt"
                                  class="mt-2"
                                />
                                <clinical-void-mark
                                  :voided="image.voided"
                                  :details="image.voidDetails"
                                  class="mt-1"
                                />
                                <div
                                  v-if="image.formattedText"
                                  class="mt-4 pa-3 bg-grey-lighten-4 rounded"
                                >
                                  <clinical-author-line
                                    :author="SYSTEM_AI_AUTHOR"
                                    label="Texto leído por"
                                    class="mb-1"
                                  />
                                  <div class="pre-wrap">
                                    {{ image.formattedText }}
                                  </div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </template>
                        </v-dialog>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Report Texts -->
                  <div
                    v-if="visible(shift.reportTexts).length > 0"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                      >
                        mdi-text-box
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">
                        Registros Escritos ({{ visible(shift.reportTexts).length }})
                      </span>
                    </div>
                    <div
                      v-for="rt in visible(shift.reportTexts)"
                      :key="rt.id"
                      class="report-text-entry pa-3 mb-2 rounded bg-grey-lighten-5"
                    >
                      <div class="d-flex flex-wrap align-center ga-1 text-caption text-medium-emphasis mb-1">
                        <clinical-author-line
                          v-if="rt.author"
                          :author="rt.author"
                          :at="rt.humanCreatedAt"
                        />
                        <span v-else>{{ rt.humanCreatedAt }}</span>
                        <clinical-record-history
                          item-type="ReportText"
                          :item-id="rt.id"
                          :edited="rt.edited"
                          :label="`Registro escrito · ${rt.humanCreatedAt}`"
                        />
                        <clinical-record-void
                          item-type="ReportText"
                          :item-id="rt.id"
                          :can-void="rt.canVoid"
                          :label="`Registro escrito · ${rt.humanCreatedAt}`"
                          correction="text"
                          :current-text="rt.text"
                        />
                      </div>
                      <clinical-void-mark
                        :voided="rt.voided"
                        :details="rt.voidDetails"
                        class="mb-1"
                      />
                      <div
                        class="text-body-2 pre-wrap"
                        :class="{ [VOIDED_CLASS]: rt.voided }"
                      >
                        {{ rt.formattedText || rt.text }}
                      </div>
                    </div>
                  </div>

                  <!-- Vital Signs -->
                  <div
                    v-if="visible(shift.vitalSignRecords).length > 0"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                        color="success"
                      >
                        mdi-heart-pulse
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">
                        Signos Vitales ({{ visible(shift.vitalSignRecords).length }})
                      </span>
                    </div>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th>Signo Vital</th>
                          <th>Valor</th>
                          <th>Hora</th>
                          <th>Registrado por</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="vsr in visible(shift.vitalSignRecords)"
                          :key="vsr.id"
                        >
                          <td>{{ vsr.vitalSignName }}</td>
                          <td class="font-weight-bold">
                            <span :class="{ [VOIDED_CLASS]: vsr.voided }">{{ vsr.formattedValue }}</span>
                            <clinical-void-mark
                              :voided="vsr.voided"
                              :details="vsr.voidDetails"
                              class="font-weight-regular"
                            />
                          </td>
                          <td class="text-medium-emphasis">
                            {{ vsr.humanRecordedAt }}
                            <clinical-record-history
                              item-type="VitalSignRecord"
                              :item-id="vsr.id"
                              :edited="vsr.edited"
                              :label="`${vsr.vitalSignName} · ${vsr.humanRecordedAt}`"
                            />
                            <clinical-record-void
                              item-type="VitalSignRecord"
                              :item-id="vsr.id"
                              :can-void="vsr.canVoid"
                              :label="`${vsr.vitalSignName} · ${vsr.humanRecordedAt}`"
                              :correction="vitalSignCorrection(vsr)"
                            />
                          </td>
                          <td class="text-medium-emphasis">
                            {{ clinicalAuthorName(vsr.author) || '—' }}
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>

                  <!-- Incidents -->
                  <div
                    v-if="visible(shift.incidentRecords).length > 0"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                        color="error"
                      >
                        mdi-alert
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold text-error">
                        Incidentes ({{ visible(shift.incidentRecords).length }})
                      </span>
                    </div>
                    <div
                      v-for="ir in visible(shift.incidentRecords)"
                      :key="ir.id"
                      class="report-text-entry pa-3 mb-2 rounded bg-grey-lighten-5"
                    >
                      <div class="d-flex align-center flex-wrap ga-1">
                        <span :class="{ [VOIDED_CLASS]: ir.voided }">
                          <strong>{{ ir.incidentDefinitionName }}</strong>
                          <span
                            v-if="ir.incidentDefinitionDescription"
                            class="text-medium-emphasis"
                          >
                            — {{ ir.incidentDefinitionDescription }}
                          </span>
                        </span>
                        <clinical-record-void
                          item-type="IncidentRecord"
                          :item-id="ir.id"
                          :can-void="ir.canVoid"
                          :label="`Incidente ${ir.incidentDefinitionName} · ${ir.humanReportedAt}`"
                        />
                      </div>
                      <clinical-void-mark
                        :voided="ir.voided"
                        :details="ir.voidDetails"
                        class="mt-1"
                      />
                      <p
                        v-if="ir.observations"
                        class="mt-1 mb-0 pre-wrap"
                        :class="{ [VOIDED_CLASS]: ir.voided }"
                      >
                        {{ ir.observations }}
                      </p>
                      <clinical-author-line
                        v-if="ir.author"
                        :author="ir.author"
                        :at="ir.humanReportedAt"
                        class="mt-1"
                      />
                      <div
                        v-else
                        class="text-caption text-medium-emphasis mt-1"
                      >
                        {{ ir.humanReportedAt }}
                      </div>
                    </div>
                  </div>

                  <!-- Tasks -->
                  <div
                    v-if="visible(shift.taskRecords).length > 0"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        size="18"
                        class="mr-1"
                        color="primary"
                      >
                        mdi-clipboard-check
                      </v-icon>
                      <span class="text-subtitle-2 font-weight-bold">
                        Tareas ({{ visible(shift.taskRecords).length }})
                      </span>
                    </div>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th>Tarea</th>
                          <th>Programada</th>
                          <th>Estado</th>
                          <th>Registrada</th>
                          <th>Registrada por</th>
                          <th>Observaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="tr in visible(shift.taskRecords)"
                          :key="tr.id"
                        >
                          <td>
                            <div
                              class="font-weight-medium"
                              :class="{ [VOIDED_CLASS]: tr.voided }"
                            >
                              {{ tr.taskName }}
                            </div>
                            <clinical-void-mark
                              :voided="tr.voided"
                              :details="tr.voidDetails"
                            />
                            <div
                              v-if="tr.taskCategoryName"
                              class="text-caption text-medium-emphasis"
                            >
                              {{ tr.taskCategoryName }}
                            </div>
                          </td>
                          <td class="text-medium-emphasis">
                            {{ tr.humanScheduledAt }}
                          </td>
                          <td>
                            <v-chip
                              size="x-small"
                              variant="tonal"
                              :color="taskStateColor(tr.aasmState)"
                            >
                              {{ taskStateLabel(tr.aasmState) }}
                            </v-chip>
                          </td>
                          <td class="text-medium-emphasis">
                            {{ tr.humanRecordedAt || '—' }}
                            <!-- A Tarea pendiente is not a registro clínico until it is recorded. -->
                            <clinical-record-history
                              v-if="tr.aasmState !== 'pending' || tr.edited"
                              item-type="TaskRecord"
                              :item-id="tr.id"
                              :edited="tr.edited"
                              :label="`${tr.taskName} · ${tr.humanScheduledAt}`"
                            />
                            <clinical-record-void
                              v-if="tr.aasmState !== 'pending'"
                              item-type="TaskRecord"
                              :item-id="tr.id"
                              :can-void="tr.canVoid"
                              :label="`${tr.taskName} · ${tr.humanScheduledAt}`"
                            />
                          </td>
                          <td class="text-medium-emphasis">
                            {{ clinicalAuthorName(tr.author) || '—' }}
                          </td>
                          <td
                            class="pre-wrap"
                            :class="{ [VOIDED_CLASS]: tr.voided }"
                          >
                            {{ tr.observations || '—' }}
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>

                  <!-- Empty state for shift (its anulados, when shown, are listed above) -->
                  <v-alert
                    v-if="totalRecords(shift) === 0 && !(showVoided && shiftVoidedCount(shift) > 0)"
                    type="warning"
                    variant="tonal"
                    density="compact"
                  >
                    No hay registros clínicos para este turno.
                  </v-alert>
                </v-card-text>
              </v-card>
            </div>
          </v-expand-transition>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.expanded-content :deep(.v-table thead th) {
  font-weight: 700;
}

.shift-card {
  cursor: pointer;
  transition: box-shadow .2s, border-color .2s;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.shift-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, .1);
}

.shift-card--expanded {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.expanded-content {
  border-top-width: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.image-thumbnail {
  cursor: pointer;
  transition: transform .2s;
}

.image-thumbnail:hover {
  transform: scale(1.02);
}

/* An anulado photo stays visible, dimmed; its "Anulado" mark says why. */
.voided-image {
  opacity: .5;
}

.report-text-entry {
  border-left: 3px solid #e0e0e0;
}

.pre-wrap {
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>
